import {
  Alert,
  Button,
  Chip,
  Group,
  Loader,
  MultiSelect,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  EMPTY_MEMORY_DATE,
  fromMemoryDate,
  MemoryDateInput,
  MemoryDateValue,
  toMemoryDatePayload,
} from 'src/Components/Memories/MemoryDateInput';
import { MEMORY_KINDS, memoryKindLabel } from 'src/Components/Memories/memoryKinds';
import { PendingStory, VoiceStoryCapture } from 'src/Components/Memories/VoiceStoryCapture';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { MemberSelector } from 'src/Components/Shared/MemberSelector';
import { MemoryImagesListEdit } from 'src/Containers/Community/CommunityHome/CommunityFeed/Memories/MemoryModal/MemoryModalEdit/MemoryImagesListEdit';
import { FeedItemFile } from 'src/Containers/Community/CommunityHome/CommunityFeed/Memories/MemoryModal/MemoryModalEdit/MemoryModalEdit.types';
import { useCreateMemory, useUpdateMemory } from 'src/Hooks/Mutations/useMemoryMutations';
import useFetchCartographyTree from 'src/Hooks/Queries/useFetchCartographyTree';
import useFetchMemory from 'src/Hooks/Queries/useFetchMemory';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { memoriesApi } from 'src/Services/API';
import { MemoryDatePrecision, MemoryKind } from 'src/Services/API/Memories.api';
import {
  trackMemoryCreated,
  trackMemoryRecordingAdded,
  trackMemoryVoiceDraftReceived,
} from 'src/Services/Mixpanel/events';

import styles from './MemoryFormPage.module.scss';

const TITLE_MAX_LENGTH = 80;
const NO_KIND = 'NONE';

/** One page, no accordion: everything a memory can hold, only the title is required. */
const MemoryFormPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const { memoryId } = useParams<{ memoryId?: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!memoryId;

  const { data: memory, isLoading: isLoadingMemory } = useFetchMemory(memoryId);
  const { data: tree } = useFetchCartographyTree();
  const { mutate: createMemory, isLoading: isCreating } = useCreateMemory();
  const { mutate: updateMemory, isLoading: isUpdating } = useUpdateMemory(memoryId ?? '');

  const [title, setTitle] = useState('');
  const [date, setDate] = useState<MemoryDateValue>(() => {
    // "Personne n'a encore raconté les années 70" pre-fills the decade.
    const decade = Number(searchParams.get('decade'));
    return decade
      ? { ...EMPTY_MEMORY_DATE, precision: MemoryDatePrecision.Decade, decade }
      : EMPTY_MEMORY_DATE;
  });
  const [kind, setKind] = useState<string>(NO_KIND);
  const [roomIds, setRoomIds] = useState<string[]>([]);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [freeNames, setFreeNames] = useState<string[]>([]);
  const [photos, setPhotos] = useState<FeedItemFile[]>([]);
  const [place, setPlace] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [story, setStory] = useState<PendingStory | null>(null);
  const [isSendingStory, setIsSendingStory] = useState(false);
  const [draftState, setDraftState] = useState<'idle' | 'listening' | 'done' | 'failed'>('idle');
  const [transcript, setTranscript] = useState<string | undefined>();
  const draftedRecording = useRef<Blob | null>(null);

  useEffect(() => {
    if (!memory) return;
    setTitle(memory.title);
    setDate(fromMemoryDate(memory.takenAt, memory.takenAtPrecision, memory.takenAtApproximate));
    setKind(memory.memoryKind ?? NO_KIND);
    setRoomIds(memory.rooms.map(room => room.id));
    setMemberIds(memory.people.map(person => person.memberId).filter((id): id is string => !!id));
    setFreeNames(
      memory.people.filter(person => !person.memberId).map(person => person.displayName)
    );
    setPhotos(memory.files.map(file => ({ id: file.id, serverFile: file })));
    setPlace(memory.place ?? '');
    setDescription(memory.description ?? '');
  }, [memory]);

  // Lucien listens to the story once and fills only the fields still empty: never what the
  // family already typed. Changing the storyteller does not ask him again.
  useEffect(() => {
    const audio = story?.recording.audio;
    if (isEdit || !assetId || !audio || draftedRecording.current === audio) return;
    draftedRecording.current = audio;
    setDraftState('listening');
    setTranscript(undefined);
    memoriesApi
      .draftFromVoice(assetId, {
        audio,
        filename: story.recording.filename.replace('dictation', 'recit'),
        language: i18n.language?.startsWith('en') ? 'en' : 'fr',
      })
      .then(draft => {
        if (draftedRecording.current !== audio) return;
        setTranscript(draft.transcript);
        setTitle(current => current.trim() || draft.title);
        setDate(current =>
          toMemoryDatePayload(current).takenAt || !draft.takenAt
            ? current
            : fromMemoryDate(draft.takenAt, draft.takenAtPrecision, draft.takenAtApproximate)
        );
        setKind(current => (current !== NO_KIND || !draft.memoryKind ? current : draft.memoryKind));
        setRoomIds(current => (current.length > 0 ? current : draft.roomIds));
        setMemberIds(current => (current.length > 0 ? current : draft.memberIds));
        setFreeNames(current => (current.length > 0 ? current : draft.freeNames));
        setDescription(current => current.trim() || draft.description);
        setDraftState('done');
        trackMemoryVoiceDraftReceived({
          has_date: !!draft.takenAt,
          rooms_count: draft.roomIds.length,
          people_count: draft.memberIds.length + draft.freeNames.length,
        });
      })
      .catch(() => {
        if (draftedRecording.current === audio) setDraftState('failed');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.recording.audio]);

  // Grouped by building: a farm can have 50 rooms, a wall of chips was unreadable.
  const roomGroups = useMemo(
    () =>
      (tree?.buildings ?? [])
        .filter(building => building.rooms.length > 0)
        .map(building => ({
          group: building.name,
          items: building.rooms.map(room => ({ value: room.id, label: room.name })),
        })),
    [tree]
  );

  if (isEdit && isLoadingMemory) return <Loader />;

  const timelinePath = `/asset/${assetId}/memories`;
  const isSaving = isCreating || isUpdating || isSendingStory;

  const handleSubmit = () => {
    setError(null);
    if (!title.trim()) {
      setError(t('memories.form.titleRequired', 'Donnez un titre au souvenir.'));
      return;
    }
    if (photos.some(photo => !photo.serverFile)) {
      setError(
        t(
          'community.photosStillUploading',
          'Les photos sont encore en cours d’envoi. Réessayez dans quelques secondes.'
        )
      );
      return;
    }

    const payload = {
      title: title.trim(),
      description: description.trim(),
      memoryKind: kind === NO_KIND ? null : (kind as MemoryKind),
      ...toMemoryDatePayload(date),
      fileIds: photos.map(photo => photo.serverFile!.id),
      roomIds,
      people: [
        ...memberIds.map(id => ({ memberId: id })),
        ...freeNames
          .map(name => name.trim())
          .filter(Boolean)
          .map(name => ({ freeName: name })),
      ],
      place: place.trim() || null,
    };
    const onError = () =>
      setError(t('memories.form.saveError', "Le souvenir n'a pas pu être enregistré. Réessayez."));

    if (isEdit) {
      updateMemory(payload, {
        onSuccess: updated => navigate(`${timelinePath}/${updated.id}`),
        onError,
      });
      return;
    }
    createMemory(payload, {
      onSuccess: async created => {
        trackMemoryCreated({
          kind: created.memoryKind ?? 'none',
          has_voice: !!story,
          drafted_by_lucien: draftState === 'done',
          source: searchParams.get('source') === 'community' ? 'community' : 'manual',
        });
        const memoryPath = `${timelinePath}/${created.id}`;
        if (!story) {
          // No story told yet: the memory page opens straight on the microphone.
          navigate(memoryPath, { state: { openRecorder: true } });
          return;
        }
        setIsSendingStory(true);
        try {
          await memoriesApi.addRecording(assetId!, created.id, {
            audio: story.recording.audio,
            filename: story.recording.filename.replace('dictation', 'recit'),
            durationSeconds: story.recording.durationSeconds,
            speakerId: story.speakerId,
            speakerName: story.speakerName,
            transcript,
            language: i18n.language?.startsWith('en') ? 'en' : 'fr',
          });
          trackMemoryRecordingAdded({ duration_seconds: story.recording.durationSeconds });
          notifications.show({
            color: 'teal',
            message: t('memories.form.created', 'Votre souvenir est dans la frise.'),
          });
          navigate(memoryPath);
        } catch {
          notifications.show({
            color: 'orange',
            message: t(
              'memories.form.storyNotSent',
              'Le souvenir est enregistré, mais pas le récit. Racontez-le à nouveau depuis sa fiche.'
            ),
          });
          navigate(memoryPath, { state: { openRecorder: true } });
        } finally {
          setIsSendingStory(false);
        }
      },
      onError,
    });
  };

  return (
    <div className={styles.container}>
      <Link to={isEdit ? `${timelinePath}/${memoryId}` : timelinePath}>
        <Button variant="transparent" leftSection={<IconArrowLeft size={18} />}>
          {t('common.back', 'Retour')}
        </Button>
      </Link>
      <PageHeader
        label={
          isEdit
            ? t('memories.form.editTitle', 'Modifier le souvenir')
            : t('memories.form.createTitle', 'Raconter un souvenir')
        }
      />

      <Stack gap="xl">
        {!isEdit && (
          <section className={styles.voiceCard}>
            <Text className={styles.voiceTitle}>
              {t('memories.form.voiceTitle', 'Racontez-le à voix haute')}
            </Text>
            <Text c="dimmed">
              {t(
                'memories.form.voiceHelp',
                'Appuyez et racontez comme à table : Lucien remplira le souvenir pour vous. Vous pouvez aussi seulement écrire.'
              )}
            </Text>
            <VoiceStoryCapture onChange={setStory} />
            {draftState === 'listening' && (
              <Group gap="sm">
                <Loader size="sm" />
                <Text>{t('memories.form.lucienListening', 'Lucien écoute votre récit…')}</Text>
              </Group>
            )}
            {draftState === 'done' && (
              <Alert color="teal">
                {t(
                  'memories.form.lucienDone',
                  'Lucien a pré-rempli le souvenir à partir de votre récit. Vérifiez et corrigez avant de l’ajouter.'
                )}
              </Alert>
            )}
            {draftState === 'failed' && (
              <Alert color="gray">
                {t(
                  'memories.form.lucienFailed',
                  'Lucien n’a pas pu lire ce récit. Il sera quand même ajouté au souvenir : remplissez le reste à la main.'
                )}
              </Alert>
            )}
          </section>
        )}

        <TextInput
          size="md"
          label={t('memories.form.title', 'Titre')}
          placeholder={t('memories.form.titlePlaceholder', 'Le mariage d’Alice et Pierre')}
          withAsterisk
          maxLength={TITLE_MAX_LENGTH}
          value={title}
          onChange={event => setTitle(event.currentTarget.value)}
        />

        <MemoryDateInput value={date} onChange={setDate} />

        <Stack gap="sm">
          <Text fw={500}>{t('memories.form.kind', 'Quel genre de souvenir ?')}</Text>
          <Chip.Group value={kind} onChange={value => setKind(value as string)}>
            <Group gap="xs">
              {MEMORY_KINDS.map(memoryKind => (
                <Chip key={memoryKind} value={memoryKind} size="md">
                  {memoryKindLabel(t, memoryKind)}
                </Chip>
              ))}
              <Chip value={NO_KIND} size="md">
                {memoryKindLabel(t, null)}
              </Chip>
            </Group>
          </Chip.Group>
        </Stack>

        {roomGroups.length > 0 && (
          <MultiSelect
            size="md"
            label={t('memories.form.rooms', 'Dans quelles pièces ?')}
            placeholder={t('memories.form.roomsPlaceholder', 'Tapez le nom d’une pièce')}
            data={roomGroups}
            value={roomIds}
            onChange={setRoomIds}
            searchable
            clearable
            hidePickedOptions
            nothingFoundMessage={t('memories.form.noRoom', 'Aucune pièce de ce nom')}
          />
        )}

        <Stack gap="sm">
          <Text fw={500}>{t('memories.form.people', 'Qui était là ?')}</Text>
          <MemberSelector
            size="md"
            value={memberIds}
            onChange={setMemberIds}
            placeholder={t('memories.form.members', 'Des membres de la maison')}
          />
          <TagsInput
            size="md"
            value={freeNames}
            onChange={setFreeNames}
            maxLength={100}
            placeholder={t(
              'memories.form.freeNames',
              'Et d’autres personnes : Mamie Odette, tonton Jean… (Entrée pour ajouter)'
            )}
          />
        </Stack>

        <Stack gap="sm">
          <Text fw={500}>{t('memories.form.photos', 'Photos')}</Text>
          <MemoryImagesListEdit photos={photos} setPhotos={setPhotos} isEdit={isEdit} hideTitle />
        </Stack>

        <TextInput
          size="md"
          label={t('memories.form.place', 'Où, plus précisément ? (facultatif)')}
          placeholder={t('memories.form.placePlaceholder', 'Sous le grand chêne')}
          maxLength={255}
          value={place}
          onChange={event => setPlace(event.currentTarget.value)}
        />

        <Stack gap={4}>
          <Text fw={500}>{t('memories.form.description', 'Racontez (facultatif)')}</Text>
          <Text size="sm" c="dimmed">
            {t(
              'memories.form.descriptionHelp',
              'Quelques mots écrits, si vous préférez ou en plus du récit.'
            )}
          </Text>
        </Stack>
        <Textarea
          size="md"
          aria-label={t('memories.form.description', 'Racontez (facultatif)')}
          autosize
          minRows={4}
          maxLength={3000}
          value={description}
          onChange={event => setDescription(event.currentTarget.value)}
        />

        {error && <Alert color="red">{error}</Alert>}

        <Group justify="flex-end">
          <Button variant="outline" size="md" onClick={() => navigate(-1)}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button size="md" onClick={handleSubmit} loading={isSaving}>
            {isEdit
              ? t('common.save', 'Enregistrer')
              : t('memories.form.submit', 'Ajouter à la mémoire de la maison')}
          </Button>
        </Group>
      </Stack>
    </div>
  );
};

export default MemoryFormPage;
